import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  headerCellStyle,
  cellStyle,
} from "../../components/tableHelpers/TableStyleUtils";
import "./TopsTables.css";

export default function TopsByCarrierTable({ model }) {
  // Shows top quotes by carrier considering both top1 and topN for every lane
  const { t } = useTranslation();

  //============================================================================================================
  // Compute table data
  //============================================================================================================
  const tableData = useMemo(() => {
    // sttats by carrier
    const stats = new Map();
    (model.normCarriers || []).forEach((c) => {
      // Iterates through normalized carriers
      stats.set(c.id, {
        name: c.name,
        totalQuotes: 0,
        top1Count: 0,
        topNCount: 0,
      }); // Initializes stats for each carrier
    });

    // For each lane, use the reference (top1 or topN) already calculated
    model.quotesByLane.forEach((laneQuotes, laneId) => {
      const ref = model.topByLane.get(laneId) || { top1: null, topN: null }; // Gets the reference values for the lane
      laneQuotes.forEach((q) => {
        const cId = q.CarrierId ?? q.Carrier?.Id; // Gets the carrier ID, handling both direct and nested structures
        if (cId == null) return;

        if (!stats.has(cId)) {
          // If the carrier is not already in stats, initialize it
          stats.set(cId, {
            name: q.CarrierName ?? String(cId),
            totalQuotes: 0,
            top1Count: 0,
            topNCount: 0,
          }); // Initializes stats for this carrier
        }

        const s = stats.get(cId); // Gets the stats for this carrier
        s.totalQuotes += 1; // Increments the total quotes for this carrier

        if (ref.top1 != null && q.BidValue === ref.top1) {
          // Checks if the quote matches the top1 value
          s.top1Count += 1; // Increments the count for top1
          s.topNCount += 1; // topN is always >= top1, so increment both
        } else if (ref.topN != null && q.BidValue <= ref.topN) {
          // Checks if the quote matches the topN value
          s.topNCount += 1; // Increments the count for topN
        }
      });
    });

    const pct = (num, den) =>
      den > 0 ? `${Math.round((num / den) * 100)}%` : "-"; // Converts count to percentage

    // Returns the final table data structure
    return (model.normCarriers || []).map((c) => {
      const s = stats.get(c.id) ?? {
        name: c.name,
        totalQuotes: 0,
        top1Count: 0,
        topNCount: 0,
      };
      return {
        name: s.name,
        top1: pct(s.top1Count, s.totalQuotes),
        top2: pct(s.topNCount, s.totalQuotes), // NTH for now is set to top2
      };
    });
  }, [model]);

  //============================================================================================================
  // Render the table
  //============================================================================================================
  return (
    // Wrapper + inner scroll container to allow horizontal scrolling only on the table
    <div className="tops-table-wrapper" style={{ marginTop: "40px" }}>
      <span
        style={{
          fontWeight: "bold",
          color: "var(--color-red-dark)",
          fontSize: "16px",
          display: "inline-block",
          marginBottom: "10px",
        }}
      >
        {t("TopQuotesByCarrier")}
      </span>

      <div className="tops-table-container">
        <table className="tops-table tops-table-carrier">
          <thead>
            <tr>
              <th style={headerCellStyle()} className="tops-col-carrier">
                {t("Carrier")}
              </th>
              <th style={headerCellStyle()} className="tops-col-small">
                {t("Top1")}
              </th>
              <th style={headerCellStyle()} className="tops-col-small">
                {t("Top2")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, idx) => (
              <tr key={idx}>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-carrier tops-cell"
                >
                  {row.name}
                </td>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-small tops-cell"
                >
                  {row.top1}
                </td>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-small tops-cell"
                >
                  {row.top2}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

TopsByCarrierTable.propTypes = {
  model: PropTypes.shape({
    normCarriers: PropTypes.array.isRequired,
    quotesByLane: PropTypes.instanceOf(Map).isRequired,
    topByLane: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
};
